import { defineStore } from "pinia";
import { PrintingOrderSupplier } from "./entity";
import { PrintingOrderSupplierList } from "./list.gen";
import type {
  PrintingOrderSupplierStore,
  PrintingOrderSupplierStoreState,
} from "./store.gen.d";
import { analytics, auth } from "@/firebase";
import { type JSONData } from "../../base_classes/dto.d";
import { Pagination } from "../../base_classes/pagination";
import { Query } from "../../base_classes/query";
import { paginationAndQueriesToCacheKey } from "../../base_classes/dto_list";

/// ///////////////////////////////////////////////////////////////////////////////////////////////////
/// WARNING! THIS FILE IS AUTOMATICALLY GENERATED, ANY CHANGES WILL BE LOST ON NEXT GENERATION
/// IF YOU WANT TO EDIT THIS FILE, REMOVE THE .gen. FROM THE FILENAME. THE FILE WILL THEN BE IGNORED.
/// ///////////////////////////////////////////////////////////////////////////////////////////////////

const printingOrderSupplierList = new PrintingOrderSupplierList();
export const usePrintingOrderSupplierStore = defineStore(
  "printingOrderSupplierStore",
  {
    state: (): PrintingOrderSupplierStoreState => ({
      activeID: null,
      cache: {},
      list: [] as PrintingOrderSupplier[],
      retrievalTimestamp: 0,
      updateKey: 0,
      storeUpdateTimeout: null,
    }),
    actions: {
      initializeStore(): void {
        this.convertList();
      },
      emitFormUpdateEvent(id: string): void {
        window.dispatchEvent(
          new CustomEvent("form-update-PrintingOrderSupplier", {
            detail: { id },
          }),
        );
      },
      emitStoreUpdateEvent(): void {
        // Clear existing timeout
        if (this.storeUpdateTimeout !== null) {
          clearTimeout(this.storeUpdateTimeout);
        }

        // Set new timeout
        this.storeUpdateTimeout = setTimeout(() => {
          window.dispatchEvent(
            new CustomEvent("store.update.PrintingOrderSupplier"),
          );
          this.storeUpdateTimeout = null;
        }, 1500);
      },
      async activate(
        printingOrderSupplier: PrintingOrderSupplier,
      ): Promise<void> {
        // Set active
        await printingOrderSupplier
          .activate(auth.firebaseUser?.uid)
          .then(() => {
            this.activeID = printingOrderSupplier.id;
          })
          .catch((error) => {
            console.error(error);
            this.activeID = null;
          });
      },
      add(printingOrderSupplier: PrintingOrderSupplier): void {
        const id = printingOrderSupplier.id;
        if (typeof id === "string" && !this.contains(id)) {
          this.list.push(printingOrderSupplier);

          // Set active
          if (this.active === null) {
            this.activeID = printingOrderSupplier.id;
          }
        } else {
          this.reinsert(printingOrderSupplier);
        }
        this.updateKey += 1;
      },
      convertList(): void {
        this.list = (this.list as PrintingOrderSupplier[]).map((x: any) => {
          const printingOrderSupplier = new PrintingOrderSupplier({});
          printingOrderSupplier.set(x as JSONData);
          return printingOrderSupplier;
        });
      },
      async delete(
        printingOrderSupplier: PrintingOrderSupplier,
      ): Promise<void> {
        await printingOrderSupplier
          .delete()
          .then(() => {
            this.remove(printingOrderSupplier);
          })
          .finally(() => {
            this.emitStoreUpdateEvent();
          });
      },
      async deleteById(id: string): Promise<void> {
        const printingOrderSupplier = (
          this.list as PrintingOrderSupplier[]
        ).find((x: PrintingOrderSupplier) => x.id === id);
        if (printingOrderSupplier !== undefined) {
          await this.delete(printingOrderSupplier);
        } else {
          throw new Error(
            "Could not find PrintingOrderSupplier with id: " + id,
          );
        }
      },
      contains(id: string): boolean {
        return (this.list as PrintingOrderSupplier[]).some(
          (x: PrintingOrderSupplier) => x.id === id,
        );
      },
      get(id: string): PrintingOrderSupplier | null {
        return (
          (this.list as PrintingOrderSupplier[]).find(
            (x: PrintingOrderSupplier) => x.id === id,
          ) || null
        );
      },
      reinsert(printingOrderSupplier: PrintingOrderSupplier): void {
        const index = (this.list as PrintingOrderSupplier[]).findIndex(
          (x: PrintingOrderSupplier) => x.id === printingOrderSupplier.id,
        );
        if (index !== -1) {
          this.list = [
            ...this.list.slice(0, index),
            printingOrderSupplier,
            ...this.list.slice(index + 1),
          ];
        } else {
          this.add(printingOrderSupplier);
        }
        this.updateKey += 1;
      },
      remove({ id }: PrintingOrderSupplier): void {
        const index = (this.list as PrintingOrderSupplier[]).findIndex(
          (x: PrintingOrderSupplier) => x.id === id,
        );
        if (index !== -1) {
          this.list = [
            ...this.list.slice(0, index),
            ...this.list.slice(index + 1),
          ];
        }
        this.updateKey += 1;
      },
      async retrieve(
        id: string,
        force = false,
      ): Promise<PrintingOrderSupplier> {
        // Check if a refresh is required
        if (!force && !this.refreshRequired) {
          const printingOrderSupplier = this.get(id);
          if (printingOrderSupplier !== null) {
            return printingOrderSupplier;
          }
        }

        // Retrieve printingOrderSupplier
        const printingOrderSupplier = new PrintingOrderSupplier({});

        // Get printingOrderSupplier
        await printingOrderSupplier.get([id]).then(async (entity) => {
          this.reinsert(entity);

          // Activate printingOrderSupplier
          await this.activate(entity);
        });

        return printingOrderSupplier;
      },
      async retrieveAll(force = false): Promise<{
        active: string | null;
        items: PrintingOrderSupplier[];
      }> {
        // Check if a refresh is required
        if (!force && !this.refreshRequired) {
          return {
            active: this.activeID,
            items: this.list as PrintingOrderSupplier[],
          };
        }

        // Clear list
        while (this.list.length > 0) {
          this.list.pop();
        }

        // Retrieve list
        await printingOrderSupplierList.getAll().then(async (list) => {
          list.forEach((printingOrderSupplier) => {
            this.reinsert(printingOrderSupplier);
          });

          // Activate printingOrderSupplier
          if (list.length > 0) {
            if (printingOrderSupplierList.active !== null) {
              this.activeID = printingOrderSupplierList.active;
            } else {
              await this.activate(list[0]);
            }
          }
        });

        // Set retrieval timestamp
        this.retrievalTimestamp = Date.now();

        // Set user property
        if (this.activeID !== null) {
          analytics.setUserProperty(
            "printing_order_supplier_id",
            this.activeID,
          );
        }

        return {
          active: this.activeID,
          items: this.list as PrintingOrderSupplier[],
        };
      },
      async retrievePaginated(
        pagination: Pagination,
        force = false,
      ): Promise<{
        pagination: Pagination;
        items: PrintingOrderSupplier[];
      }> {
        // Check pagination
        let newPagination = new Pagination(pagination).deepCopy();

        // Get cachekey
        const cacheKey = pagination.toCacheKey();

        // Check if a refresh is required
        if (!force && this.cache[cacheKey]) {
          const cached = this.cache[cacheKey];
          return {
            pagination: cached.pagination.deepCopy(),
            items: cached.ids.map(
              (id: string) => this.get(id) as PrintingOrderSupplier,
            ),
          };
        }

        // Retrieve list
        const items = await printingOrderSupplierList
          .getPaginated([], pagination)
          .then(async ({ items, pagination }) => {
            items.forEach((printingOrderSupplier) => {
              this.reinsert(printingOrderSupplier);
            });

            // Set new pagination
            newPagination = pagination.deepCopy();

            return items;
          });

        // Set cache
        this.cache[cacheKey] = {
          ids: items.map((x: PrintingOrderSupplier) => x.id),
          pagination: newPagination,
          retrievalTimestamp: Date.now(),
        };

        // Set retrieval timestamp
        this.retrievalTimestamp = Date.now();

        return {
          pagination: newPagination,
          items,
        };
      },
      async query(
        queries: Query[],
        pagination: Pagination,
        force = false,
      ): Promise<{
        pagination: Pagination;
        items: PrintingOrderSupplier[];
      }> {
        // Check pagination
        let newPagination = new Pagination(pagination).deepCopy();

        // Get cachekey
        const cacheKey = paginationAndQueriesToCacheKey(pagination, queries);

        // Check if a refresh is required
        if (!force && this.cache[cacheKey]) {
          const cached = this.cache[cacheKey];
          return {
            pagination: cached.pagination.deepCopy(),
            items: cached.ids.map(
              (id: string) => this.get(id) as PrintingOrderSupplier,
            ),
          };
        }

        // Retrieve list
        const items = await printingOrderSupplierList
          .query(queries, pagination)
          .then(async ({ items, pagination }) => {
            items.forEach((printingOrderSupplier) => {
              this.reinsert(printingOrderSupplier);
            });

            // Set new pagination
            newPagination = pagination.deepCopy();

            return items;
          });

        // Set cache
        this.cache[cacheKey] = {
          ids: items.map((x: PrintingOrderSupplier) => x.id),
          pagination: newPagination.deepCopy(),
          retrievalTimestamp: Date.now(),
        };

        // Set retrieval timestamp
        this.retrievalTimestamp = Date.now();

        return {
          pagination: newPagination,
          items: items,
        };
      },
      async search(
        query: string,
        pagination: Pagination,
        force = false,
      ): Promise<{
        pagination: Pagination;
        items: PrintingOrderSupplier[];
      }> {
        // Check pagination
        let newPagination = new Pagination(pagination).deepCopy();

        // Get cachekey
        const cacheKey =
          query.replaceAll(" ", "_") + "-" + pagination.toCacheKey();

        // Check if a refresh is required
        if (!force && this.cache[cacheKey]) {
          const cached = this.cache[cacheKey];
          return {
            items: cached.ids.map(
              (id: string) => this.get(id) as PrintingOrderSupplier,
            ),
            pagination: cached.pagination.deepCopy(),
          };
        }

        // Retrieve list
        const items = await printingOrderSupplierList
          .search(query, pagination)
          .then(async ({ items, pagination }) => {
            items.forEach((printingOrderSupplier) => {
              this.reinsert(printingOrderSupplier);
            });

            // Set new pagination
            newPagination = pagination.deepCopy();

            return items;
          });

        // Set cache
        this.cache[cacheKey] = {
          ids: items.map((x: PrintingOrderSupplier) => x.id),
          pagination: newPagination.deepCopy(),
          retrievalTimestamp: Date.now(),
        };

        // Set retrieval timestamp
        this.retrievalTimestamp = Date.now();

        return {
          items: items,
          pagination: newPagination,
        };
      },
      async update(
        printingOrderSupplier: PrintingOrderSupplier,
      ): Promise<string> {
        // Update printingOrderSupplier
        await printingOrderSupplier.update();

        // Reinsert printingOrderSupplier
        this.reinsert(printingOrderSupplier);

        // Activate printingOrderSupplier
        if (this.active === null) {
          await this.activate(printingOrderSupplier);
        }

        this.emitStoreUpdateEvent();

        return printingOrderSupplier.id;
      },
    },
    getters: {
      active(): PrintingOrderSupplier | null {
        if (this.activeID === null) {
          return null;
        }

        const printingOrderSupplier = (
          this.list as PrintingOrderSupplier[]
        ).find((x: PrintingOrderSupplier) => x.id === this.activeID);
        if (printingOrderSupplier === undefined) {
          return null;
        }

        return printingOrderSupplier;
      },
      refreshRequired(): boolean {
        return (
          this.retrievalTimestamp === 0 ||
          this.retrievalTimestamp < Date.now() - 30 * 60000 ||
          this.list.length === 0
        );
      },
    },
  },
);

// Export a function to create a new instance of the store
export function createPrintingOrderSupplierStore(): PrintingOrderSupplierStore {
  const store = usePrintingOrderSupplierStore();
  // Run the initializeStore action when the store is created
  store.initializeStore();
  return store as unknown as PrintingOrderSupplierStore; // TODO: Fix this
}
